import React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getMessages, sendMessage } from '../../utils/services/chatFunctions'
import ArticleShort from '../shared/ArticleShort'
import { fetchUserPublicInfo } from '../../utils/services/fetchUser'
import { fetchOneThing } from '../../utils/services/fetchThing'
import TextBox from './TextBox'

const ChatDetails = ({ userInfo, activeConversation, stuffCategories }) => {
    // Préparer QueryClient pour l'invalidation des requêtes
    const queryClient = useQueryClient()

    // Calcul de `sellerId` avant `useQuery` pour éviter les erreurs
    const sellerId = activeConversation
        ? activeConversation.participants[0]._id === userInfo._id
            ? activeConversation.participants[1]._id
            : activeConversation.participants[0]._id
        : null

    // Récupération des messages de la conversation
    const {
        data: messages,
        isLoading: isLoadingMessages,
        isError: isErrorMessages,
    } = useQuery({
        queryKey: ['conversationMessages', activeConversation?._id],
        queryFn: () => getMessages(activeConversation._id),
        enabled: !!activeConversation,
        staleTime: 5 * 60 * 1000,
        retry: 0,
        onError: (err) => {
            console.error('Error fetching messages:', err)
        },
    })

    // Récupération des informations publiques du destinataire
    const {
        data: recipientInfo,
        isLoading: isLoadingRecipientInfo,
        isError: isErrorRecipientInfo,
    } = useQuery({
        queryKey: ['recipientInfo', sellerId],
        queryFn: () => fetchUserPublicInfo(sellerId),
        enabled: !!sellerId,
        staleTime: 5 * 60 * 1000,
        retry: 0,
        onError: (err) => {
            console.error('Error fetching recipient info:', err)
        },
    })

    // Récupération des informations d'un item
    const {
        data: item,
        isLoading: isLoadingItem,
        isError: isErrorItem,
    } = useQuery({
        queryKey: ['itemInfo', activeConversation?.thingId],
        queryFn: () => fetchOneThing(activeConversation.thingId),
        enabled: !!activeConversation,
        staleTime: 5 * 60 * 1000,
        retry: 0,
        onError: (err) => {
            console.error('Error fetching recipient info:', err)
        },
    })

    // Gestion des états de chargement et d'erreur
    if (isLoadingMessages || isLoadingRecipientInfo) {
        return <p>Chargement des messages...</p>
    }
    if (isLoadingItem) {
        return <p>Chargement des objets...</p>
    }

    if (isErrorMessages || isErrorRecipientInfo) {
        return <p>Erreur lors du chargement des messages</p>
    }
    if (isErrorItem) {
        return <p>Erreur lors du chargement des objects</p>
    }

    const handleSendMessage = async (message) => {
        try {
            await sendMessage(activeConversation._id, message)
        } catch (err) {
            console.log(err)
        } finally {
            // Invalider le cache des messages pour forcer le rechargement
            queryClient.invalidateQueries([
                'conversationMessages',
                activeConversation._id,
            ])
        }
    }

    return (
        <div>
            <h2>Conversation</h2>
            {activeConversation ? (
                <div>
                    {item && (
                        <ArticleShort
                            sellOrBuySection="chat"
                            stuffCategories={stuffCategories}
                            item={item} // `thingId` doit être un objet complet
                        />
                    )}
                    {messages.map((msg, index) => (
                        <div key={index}>
                            <p>
                                <strong>
                                    {msg.senderId === userInfo._id
                                        ? userInfo.pseudo
                                        : recipientInfo.pseudo}
                                </strong>
                                : {msg.content}
                            </p>
                        </div>
                    ))}
                    <TextBox onSendMessage={handleSendMessage} />
                </div>
            ) : (
                <p>Aucune conversation trouvée</p>
            )}
        </div>
    )
}

export default ChatDetails
